import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownWideNarrow, ArrowUpWideNarrow, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import { cn, getDateTime, sortData } from 'lib/utils';
import { LabReport } from 'services/interpret-lab-report/type';
import Icon from 'components/icon/Icon';
import { PersonalisedAIReport } from 'services/personalised-ai-report/type';
import useAuthStore from 'store/auth';
import { SortOrder } from 'types';

type ReportData = LabReport | PersonalisedAIReport;

interface PreviousReportsProps {
  tokenId: string;
  setTokenId: Dispatch<SetStateAction<string>>;
  reportData: ReportData[];
  setReportData: Dispatch<SetStateAction<ReportData[]>>;
  scrollViewElement: React.ReactNode;
  isLoading: boolean;
  reportType?: 'lab' | 'ai' | 'symptom';
}

export default function PreviousReports({
  reportData,
  tokenId,
  isLoading,
  reportType = 'ai',
  setTokenId,
  setReportData,
  scrollViewElement,
}: PreviousReportsProps) {
  const { t } = useTranslation();

  const locale = useAuthStore((store) => store.locale);

  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const onSort = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);

    let data = sortData<ReportData>({
      data: reportData,
      key: 'created_at',
      order: newOrder,
    });
    setReportData(data);
  };

  return (
    <div className='p-4 !mb-4 border border-blue-500 rounded-xl max-w-xl mx-auto'>
      <h1 className='font-semibold text-center text-blue-500 uppercase'>
        {t('previous_reports')}
      </h1>

      <div className='mt-2 mb-4'>
        <Button
          variant='ghost'
          className='text-sm font-medium text-blue-500 capitalize border border-blue-500 hover:bg-blue-500/5 hover:text-blue-500 h-7'
          onClick={onSort}
        >
          {sortOrder === 'desc' ? (
            <ArrowDownWideNarrow className='mr-1 size-4' />
          ) : (
            <ArrowUpWideNarrow className='mr-1 size-4' />
          )}{' '}
          {sortOrder}
        </Button>
      </div>

      <ScrollArea
        className={cn(
          'max-lg:min-h-[50vh] h-full lg:h-[calc(100vh-31rem)] xl:h-[calc(100vh-32.5rem)]',
          reportType === 'ai' && locale === 'es' && 'lg:h-[calc(100vh-37rem)]',
          reportType === 'lab' && locale === 'es' && 'lg:h-[calc(100vh-34rem)]',
          reportType === 'symptom' &&
            locale === 'es' &&
            'lg:h-[calc(100vh-32.5rem)]'
        )}
      >
        {isLoading ? (
          <Loader2
            className='stroke-1 text-primary animate-spin size-6'
            role='progressbar'
          />
        ) : (
          <div>
            {reportData?.map((report) => {
              const { date, time } = getDateTime(
                report.created_at,
                'MM-dd-yyyy'
              );
              return (
                <div
                  key={report.token_id}
                  className='relative flex items-end h-10 cursor-pointer'
                  onClick={() => setTokenId(report.token_id)}
                >
                  <div className='absolute h-8 -top-1 -left-0.5 -z-10'>
                    <Icon icon='history-line' size={38} />
                  </div>

                  <div
                    className={cn(
                      'font-medium leading-tight flex gap-x-2 w-full py-1.5 rounded-3xl px-2',
                      tokenId === report.token_id && 'bg-blue-600/60 text-white'
                    )}
                  >
                    <Icon icon='radio' size={20} />
                    <p>{date}</p>
                    <p
                      className={cn(
                        tokenId === report.token_id
                          ? 'text-white'
                          : 'text-blue-500'
                      )}
                    >
                      {time}
                    </p>
                  </div>
                </div>
              );
            })}

            {scrollViewElement}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
